/* ────────────────────────────  Update-bar parser  ────────────────────────────
 * Issue #91 — extracted VERBATIM from src/components/TodayTab.tsx (pure move,
 * zero logic changes) so the update-bar fast-path parser is testable in
 * isolation and pinned by a golden-input regression corpus
 * (src/lib/__tests__/updateBarParser.corpus.test.ts).
 *
 * Every parser fix since April 2026 was verified only by typing three inputs
 * into the live app, which is why fixes kept breaking each other (9+ patch
 * sessions documented). These functions are pure but were trapped inside the
 * ~11,900-line TodayTab.tsx. This module is the permanent regression harness.
 *
 * ⚠️ PURE-MOVE CONTRACT: any behavioral "improvement" spotted here is an ISSUE,
 * not a commit. Do not "fix" anything in this file without its own PR + a
 * corpus update. TodayTab.tsx imports these back and re-exports the ones the
 * existing tests reference, so the app's behavior stays byte-identical.
 */

/** Format a Date as YYYY-MM-DD using local timezone. */
export function formatLocalDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/**
 * Strong task-verb prefixes. If a title STARTS with one of these (whole-word),
 * it's a mom errand/action, not an event — even if a known kid's name appears.
 *
 * Used by:
 *  - classifyEntry (initial AI-output classification)
 *  - applyChildBackupDetection (BL-2 guard: don't flip errand tasks → events
 *    just because a kid name is present)
 *
 * Additions beyond the original classifyEntry list:
 *   bring, fix, find, look up, search, research, charge, refill, replace,
 *   choose, pick out, do
 *
 * Deliberately NOT included:
 *   "take" — genuinely ambiguous; "take Carson to swim" is a kid event
 *   where the mom is the chauffeur but the underlying activity is Carson's.
 */
const TASK_VERB_PREFIXES = [
  "buy", "get", "grab", "pick up", "drop off", "pickup", "dropoff",
  "call", "email", "text", "message", "reply", "respond",
  "pack", "unpack", "prep", "prepare", "finish", "complete", "submit", "send",
  "return", "schedule", "book", "reserve", "order", "cancel",
  "wash", "clean", "fold", "empty", "take out",
  "pay", "renew", "sign up", "register", "rsvp",
  "read", "write", "review", "edit", "print",
  "water", "feed", "walk the", "ask", "remind", "tell",
  "bring", "fix", "find", "look up", "search", "research",
  "charge", "refill", "replace", "choose", "pick out", "do",
];

/** True when `title` begins with a TASK_VERB_PREFIXES entry as a whole word. */
export function titleStartsWithTaskVerb(title: string): boolean {
  if (!title) return false;
  const lower = title.trim().toLowerCase();
  for (const verb of TASK_VERB_PREFIXES) {
    if (lower === verb || lower.startsWith(verb + " ")) return true;
  }
  return false;
}

/** Fast-path category resolver. Mirrors applyChildBackupDetection's
 *  BL-2 guard: a known/detected kid promotes the item to "event"
 *  UNLESS the title is an errand task (titleStartsWithTaskVerb),
 *  in which case the pre-classified category (typically "task") is
 *  preserved.
 *
 *  Companion to applyChildBackupDetection's branch A (~L1192) and
 *  branch B (~L1217) guards. Three sites total, same invariant:
 *  "kid name + errand verb ⇒ task".
 */
export function resolveFastPathCategory(
  title: string,
  preClassified: "event" | "task",
  fastPathKid: string | null,
): "event" | "task" {
  if (!fastPathKid) return preClassified;
  if (titleStartsWithTaskVerb(title)) return preClassified;
  return "event";
}

/**
 * Normalize free-text time input to the canonical "H:MM AM/PM" format used by
 * every other badge in the app. Examples:
 *   "3pm" → "3:00 PM"
 *   "3:30pm" → "3:30 PM"
 *   "8am" → "8:00 AM"
 *   "10:30 am" → "10:30 AM"
 *   "14:00" → "2:00 PM"
 * Falls back to the original (trimmed) string when it can't be parsed.
 */
export function normalizeTime(input: string): string {
  if (!input) return input;
  const raw = input.trim();
  if (!raw) return raw;

  // Match: hours, optional :minutes, optional am/pm
  // Trailing "m" is optional so single-letter shorthand like "10:15a" / "10p"
  // is recognized as AM/PM (and not silently treated as 24h).
  const m = raw.match(/^(\d{1,2})(?::(\d{1,2}))?\s*([ap]\.?m?\.?)?$/i);
  if (!m) return raw;

  let hours = parseInt(m[1], 10);
  const minutes = m[2] ? parseInt(m[2], 10) : 0;
  const ampmRaw = m[3]?.toLowerCase().replace(/\./g, "");

  if (Number.isNaN(hours) || Number.isNaN(minutes)) return raw;
  if (hours < 0 || hours > 23) return raw;
  if (minutes < 0 || minutes > 59) return raw;

  let ampm: "AM" | "PM";
  // Accept "a"/"am"/"p"/"pm" (with or without dots) as ampm markers.
  if (ampmRaw === "am" || ampmRaw === "a" || ampmRaw === "pm" || ampmRaw === "p") {
    const isAm = ampmRaw === "am" || ampmRaw === "a";
    ampm = isAm ? "AM" : "PM";
    if (hours === 12) hours = ampm === "AM" ? 0 : 12;
    if (hours > 12) return raw; // "13pm" makes no sense
  } else {
    // 24-hour input like "14:00" or just "8"
    ampm = hours >= 12 ? "PM" : "AM";
  }

  let displayHour = hours % 12;
  if (displayHour === 0) displayHour = 12;

  return `${displayHour}:${String(minutes).padStart(2, "0")} ${ampm}`;
}

/* ─────────────────────────  Manual fast-path parser  ─────────────────────────
 * Detects simple single-item updates the user types into the update bar so we
 * can write straight to weekly_schedules and skip the kin-chat round-trip.
 * Returns null when the input looks complex (multiple items, edits, deletes,
 * questions) — in that case the caller falls through to the AI merge.
 */
export interface SimpleAddParsed {
  title: string;
  /** Display string in canonical "H:MM AM/PM" format, "TBD", or "all-day"
   *  (matches the ALL DAY badge convention read by isAllDayTime()). */
  time: string;
  /** Target date in YYYY-MM-DD (local). */
  dateIso: string;
  /** "event" when a time or an all-day marker was given, "task" otherwise. */
  category: "event" | "task";
}

const DAY_NAME_TO_OFFSET: Record<string, number> = {
  sunday: 0, sun: 0,
  monday: 1, mon: 1,
  tuesday: 2, tue: 2, tues: 2,
  wednesday: 3, wed: 3,
  thursday: 4, thu: 4, thur: 4, thurs: 4,
  friday: 5, fri: 5,
  saturday: 6, sat: 6,
};

/* ─────────────────────────  Shared day-word vocabulary  ─────────────────────────
 * Issue #92 — ONE definition of the day words the fast-path recognizes, consumed
 * by every site that needs them (findDayWord, isDirtyResidueTitle,
 * splitMultiEventInput). Before this, three near-identical inline regexes were
 * maintained independently — a latent divergence bug class (add an alias to one,
 * forget the others, and the splitter/residue-gate/date-resolver silently
 * disagree). Now a new alias is added in exactly one place.
 *
 * The two sets are kept SEPARATE because findDayWord() resolves them to a date
 * differently: RELATIVE_DAY_WORDS ("today"/"tomorrow"/…) map to an offset from
 * now, WEEKDAY_WORDS map through DAY_NAME_TO_OFFSET. The residue gate and the
 * splitter only ask "is any day word present?", so they use the combined
 * ANY_DAY_WORD_RE. WEEKDAY_WORDS is DERIVED from DAY_NAME_TO_OFFSET so the word
 * list and the offset map can never drift out of sync. */
const RELATIVE_DAY_WORDS = [
  "today", "tonight", "tomorrow", "tmrw", "tmw",
  "this morning", "this afternoon", "this evening",
] as const;
const WEEKDAY_WORDS: readonly string[] = Object.keys(DAY_NAME_TO_OFFSET);

/** Build a case-insensitive whole-word alternation regex from a word list.
 *  No `g` flag, so the returned regex carries no `lastIndex` state and is safe
 *  to reuse across `.test()` / `.match()` calls. Whole-word `\b` anchoring means
 *  alternation order doesn't affect which token matches (e.g. "tue" can't match
 *  inside "tuesday"), so a derived, unordered word list is behavior-identical to
 *  the original hand-ordered regexes. */
function dayWordRegex(words: readonly string[]): RegExp {
  return new RegExp(`\\b(${words.join("|")})\\b`, "i");
}

/** today / tonight / tomorrow / tmrw / tmw — relative words only. */
const RELATIVE_DAY_WORD_RE = dayWordRegex(RELATIVE_DAY_WORDS);
/** Weekday names + abbreviations only (no relative words). */
const WEEKDAY_WORD_RE = dayWordRegex(WEEKDAY_WORDS);
/** Every day word the fast-path recognizes (relative + weekday). */
const ANY_DAY_WORD_RE = dayWordRegex([...RELATIVE_DAY_WORDS, ...WEEKDAY_WORDS]);

/** Find a relative day word ("today", "tomorrow", "monday"…) in the text and
 *  return { dateIso, range } so the caller can strip it from the title.
 *  Returns null if no day word is found — caller defaults to today. */
export function findDayWord(text: string): { dateIso: string; range: [number, number] } | null {
  const easy = text.match(RELATIVE_DAY_WORD_RE);
  if (easy) {
    const word = easy[1].toLowerCase();
    const d = new Date();
    if (word === "tomorrow" || word === "tmrw" || word === "tmw") d.setDate(d.getDate() + 1);
    return { dateIso: formatLocalDate(d), range: [easy.index!, easy.index! + easy[0].length] };
  }
  const wk = text.match(WEEKDAY_WORD_RE);
  if (wk) {
    const want = DAY_NAME_TO_OFFSET[wk[1].toLowerCase()];
    if (want === undefined) return null;
    const today = new Date();
    let diff = (want - today.getDay() + 7) % 7;
    if (/\bnext\s+$/i.test(text.slice(0, wk.index!))) {
      if (diff === 0) diff = 7;
    }
    const d = new Date(today);
    d.setDate(today.getDate() + diff);
    let start = wk.index!;
    const nextMatch = text.slice(0, start).match(/\bnext\s+$/i);
    if (nextMatch) start -= nextMatch[0].length;
    return { dateIso: formatLocalDate(d), range: [start, wk.index! + wk[0].length] };
  }
  return null;
}

/** Find a clock-style time anywhere in the text. Returns canonical
 *  "H:MM AM/PM" form plus the matched range, or null when no time is present. */
export function findTime(text: string): { time: string; range: [number, number] } | null {
  // Require AM/PM (full or single-letter shorthand "a"/"p") OR a colon so
  // plain digits (e.g., "5 kids") don't match. The "m" in "am"/"pm" is
  // optional so "10:15a", "10p", "8a swim" all parse cleanly.
  const re = /\b(?:at|@)?\s*((?:1[0-2]|0?[1-9])(?::[0-5]\d)?\s*(?:[ap]\.?m?\.?)|(?:[01]?\d|2[0-3]):[0-5]\d|(?:1[0-2]|[1-9])(?:[0-5]\d)\s*(?:[ap]\.?m?\.?))\b/i;
  const m = text.match(re);
  if (m && m.index !== undefined) {
    let raw = m[1].replace(/\s+/g, "");
    const compact = raw.match(/^(\d{1,2})(\d{2})([ap]\.?m?\.?)$/i);
    if (compact) raw = `${compact[1]}:${compact[2]}${compact[3]}`;
    const normalized = normalizeTime(raw);
    if (/^\d{1,2}:\d{2}\s+(AM|PM)$/.test(normalized)) {
      return { time: normalized, range: [m.index, m.index + m[0].length] };
    }
  }

  // Bare-hour fallback: fires only when no AM/PM marker is present and an
  // explicit "at" or "@" prefix is provided ("brunch at 11", "dinner @ 6").
  // AM/PM rule: 1–6 → PM, 7–11 → AM, 12 → PM, 0 → reject.
  const bareRe = /(?:^|\s)(at|@)\s+(\d{1,2})\b(?!\s*[:apAP])/i;
  const bm = text.match(bareRe);
  if (bm && bm.index !== undefined) {
    const hour = parseInt(bm[2], 10);
    if (hour === 0 || hour > 12) return null;
    let ampm: "AM" | "PM";
    if (hour === 12) ampm = "PM";
    else if (hour >= 7) ampm = "AM";   // 7–11
    else ampm = "PM";                  // 1–6
    const normalized = `${hour}:00 ${ampm}`;
    const start = bm.index + bm[0].indexOf(bm[1]);
    return { time: normalized, range: [start, bm.index + bm[0].length] };
  }

  return null;
}

/** Strong event nouns. Extracted to module scope in #591 so classifyEntry and
 *  stripBareCommandVerb read the SAME vocabulary — a bare "schedule"/"book"
 *  is only treated as a command verb when what follows is recognisably an
 *  appointment, and that judgement must not drift from the one classifyEntry
 *  makes. Substring-matched, not word-boundary matched: "appt" has to catch
 *  "appts", and "dinner with" is a phrase. Kept byte-identical to the inline
 *  list it replaced. */
const EVENT_KEYWORDS = [
  "appointment", "appt", "meeting", "standup", "stand-up", "sync", "1:1",
  "game", "practice", "tournament", "match", "scrimmage",
  "class", "lesson", "session", "workshop", "conference",
  "party", "shower", "wedding", "funeral", "service",
  "dinner with", "lunch with", "breakfast with", "coffee with", "drinks with",
  "flight", "train", "pickup time", "drop-off time",
  "playdate", "birthday", "dentist", "doctor", "therapy", "checkup", "check-up",
  "interview", "call with", "zoom with",
];

/** True when the text contains one of EVENT_KEYWORDS. Case-insensitive. */
export function containsAppointmentNoun(text: string): boolean {
  const lower = (text || "").toLowerCase();
  return EVENT_KEYWORDS.some((keyword) => lower.includes(keyword));
}

/** Classify an entry as an event or a task based on keywords.
 *  Tasks: things the user does (buy, call, email, pick up, drop off, etc.)
 *  Events: things that happen at a specific time (appointment, meeting, game, practice, etc.)
 *  When ambiguous, falls back to time-based heuristic. */
export function classifyEntry(
  title: string,
  time: string,
  leadsWithNoun = false
): "event" | "task" {
  const lower = title.toLowerCase().trim();

  // A successfully parsed clock time always wins over the task-verb
  // short-circuit below: "call the dentist" (no time) is an errand task, but
  // "call tonight at 9pm" schedules that call for a specific moment, which
  // makes it an event regardless of the leading task verb. So the task-verb
  // short-circuit only fires when no real clock time was parsed — time is
  // "TBD" (vague/absent) or "all-day" (a day-level marker, not a moment).
  const hasRealTime = time !== "TBD" && time !== "all-day";

  // Strong task verbs — if the title STARTS with one of these AND no real
  // time was parsed, it's a task. List lives at module scope
  // (TASK_VERB_PREFIXES) so applyChildBackupDetection can share it for the
  // BL-2 errand-task guard.
  //
  // `leadsWithNoun` opts out (#591). The caller sets it when it has already
  // established that the leading word is part of a compound NOUN rather than
  // a verb — "book fair", where stripBareCommandVerb deliberately kept the
  // "book". Without it, "Book fair" tripped this short-circuit purely because
  // "book" is spelled like a task verb, and an all-day TASK renders NOWHERE
  // on the day card (scheduleItems takes category "event"; taskItems excludes
  // all-day — TodayTab.tsx ~L4596-4608). The card would silently not exist,
  // which is worse than the wrong title #591 set out to fix.
  //
  // Default is false, so every existing caller keeps its exact behavior —
  // "pack for camp" with an all-day marker is still a task.
  if (!hasRealTime && !leadsWithNoun && titleStartsWithTaskVerb(title)) {
    return "task";
  }


  // Strong event nouns — if title contains any of these, it's an event.
  // List lives at module scope (EVENT_KEYWORDS) so stripBareCommandVerb can
  // share it via containsAppointmentNoun (#591). Two hand-synced copies of one
  // vocabulary is this codebase's most common defect shape — see the #92 note
  // on the day-word lists for the same lesson.
  if (containsAppointmentNoun(lower)) {
    return "event";
  }

  // Standalone meal nouns — matched with word boundaries to avoid tripping on
  // "lunchbox", "breakfasts", etc. Kept separate from the substring list above
  // intentionally (see JUDGMENT CALL note).
  //
  // #610: "dinner" and "supper" were missing, so the two lists disagreed about
  // which meals count. EVENT_KEYWORDS already carries the PHRASE form
  // "dinner with", so "dinner with grandma friday" was an event while a bare
  // "dinner this evening" fell through to the TBD fallback and committed as a
  // TASK — same sentence shape as "lunch this evening", opposite category,
  // purely because of the omission. The checkbox is the visible half; the
  // silent half is that needsTimeAnswer() returns false for a task, so the
  // "What time?" Quick Question that would recover the TBD never fires either.
  //
  // Word-boundary matched like its neighbours, deliberately: `\bdinner\b`
  // leaves "dinnertime routine" and "dinners" alone exactly the way `\blunch\b`
  // already leaves "lunchbox" alone. Do NOT move these into EVENT_KEYWORDS —
  // that list is substring-matched and would force every "dinnertime" to event.
  const standaloneMealNouns = ["brunch", "lunch", "breakfast", "dinner", "supper"];
  for (const noun of standaloneMealNouns) {
    if (new RegExp(`\\b${noun}\\b`, "i").test(lower)) {
      return "event";
    }
  }

  // Ambiguous: time-based fallback. A real clock time or an all-day marker
  // both mean the item is scheduled for a moment/day, not an open-ended
  // to-do — only a genuinely vague/absent (TBD) time falls back to "task".
  if (time === "TBD") return "task";
  return "event";
}

/** Pure residue quality gate for a fast-path title. Returns { dirty, check } —
 *  dirty is true when leftover text in the finished title reveals a hidden
 *  SECOND event or a mangled single parse, meaning we must NOT silent-commit
 *  and should instead route to the AI path (which always surfaces a review
 *  chip). Each check names the July 8, 2026 production bug it closes (Issue
 *  #88). Keep pure + side-effect-free so it's trivially testable. */
export function isDirtyResidueTitle(title: string): { dirty: boolean; check?: string } {
  const t = (title || "").trim();

  // (a) A surviving day word means a SECOND appointment is hiding in the title
  //     — findDayWord only strips the FIRST day. July 8: "…thursday, i have a
  //     nail apt friday" committed ONE event whose title still held "Friday".
  if (ANY_DAY_WORD_RE.test(t)) return { dirty: true, check: "day-word-residue" };

  // (b) Orphaned comma — the one-comma / no-"and" splice the multi-event
  //     splitter is blind to by design. July 8: title kept " , a nail apt".
  if (/(^|\s),|,(\s|$)/.test(t)) return { dirty: true, check: "orphan-comma" };
  // (b) Standalone "and" (leading, trailing, or floating) — same hidden-second-
  //     event smell as the orphan comma above.
  if (/(^|\s)and(\s|$)/i.test(t)) return { dirty: true, check: "orphan-and" };

  // (c) Dangling copula — hallmark of a mangled single parse, not a clean noun
  //     phrase. July 8: "…swim practice is on the schedule for tomorrow at 7pm"
  //     committed title "Swim practice the schedule for" (standalone "is").
  if (/\b(is|are|was|be)\b/i.test(t)) return { dirty: true, check: "dangling-copula" };
  // (c) Dangling connector — title ending in a preposition/article means the
  //     real object got stripped away (same July 8 "…the schedule for" bug).
  if (/\b(for|to|on|the|at|with)$/i.test(t)) return { dirty: true, check: "dangling-connector" };

  // (d) Intent-verb leftovers that filler-stripping missed anywhere in the
  //     title — "make sure the sitter…", "need diapers", "needs to sign up".
  if (/\b(make sure|needs? to|need)\b/i.test(t)) return { dirty: true, check: "intent-verb" };

  return { dirty: false };
}

/* ─────────────────────────  Shared conversational-filler stripper  ───────────
 * Issue #113 — ONE definition of the "I have to / need to / don't forget /
 * make sure / we have a …" filler list, extracted VERBATIM from
 * parseSimpleManualAdd so every title-producing path can share it. Before this,
 * only the update-bar fast-path stripped filler; detectRecurrence and
 * detectFarFutureEvent (TodayTab.tsx) wrote the raw leftover as the title, so
 * "i have dance class every monday" committed the title "I have dance class"
 * (July 9, 2026 on-device bug).
 *
 * PURE + side-effect-free so it stays trivially testable and byte-identical to
 * the code it replaced (the golden corpus pins parseSimpleManualAdd unchanged). */
const _FILLER_PATTERNS: RegExp[] = [
  /\bI need to\b/gi,
  /\bI have to\b/gi,
  /\bI gotta\b/gi,
  /\bI have\b/gi,        // "I have karate" → "karate"
  /\bI'm gonna\b/gi,
  /\bI'm going to\b/gi,
  /\bI'll\b/gi,
  /\bI'm\b/gi,
  /\bgoing to\b/gi,
  /\bgotta\b/gi,
  /\bneed to\b/gi,
];

/** Prefix-only filler patterns — stripped from the very beginning of the input
 *  (case-insensitive). Longer phrases listed first so we don't strip the
 *  shorter variant prematurely (e.g., "we have a" before "we have"). */
const _PREFIX_FILLER_PATTERNS: RegExp[] = [
  /^\s*need to remember to\b/i,
  /^\s*need to remember\b/i,
  /^\s*gotta remember\b/i,
  /^\s*don'?t forget to\b/i,
  /^\s*don'?t forget\b/i,
  /^\s*remember to\b/i,
  /^\s*remember\b/i,
  /^\s*make sure to\b/i,
  /^\s*make sure\b/i,
  /^\s*we'?re going to\b/i,
  /^\s*we are going to\b/i,
  /^\s*we'?ve got\b/i,
  /^\s*we got\b/i,
  /^\s*we have a\b/i,
  /^\s*we have\b/i,
  /^\s*we need to\b/i,
  /^\s*we need\b/i,
  /^\s*there'?s a\b/i,
  /^\s*there is a\b/i,
  /^\s*let'?s\b/i,
  /^\s*gotta\b/i,
  /^\s*schedule an\b/i,
  /^\s*schedule a\b/i,
  /^\s*schedule\b/i,
  /^\s*book an\b/i,
  /^\s*book a\b/i,
  // Bare "book" is NOT here — it needs evidence first. See
  // _BARE_COMMAND_VERBS / stripBareCommandVerb below (#591).
  /^\s*add an\b/i,
  /^\s*add a\b/i,
  /^\s*add\b/i,
  /^\s*got an\b/i,
  /^\s*got a\b/i,
  /^\s*after that\b/i,
  /^\s*then\b/i,
  /^\s*also\b/i,
];

/** Bare command verbs that need evidence before they're stripped.
 *
 *  ── Why this list is exactly one word long ─────────────────────────────────
 *
 *  Three bare verbs were being stripped unconditionally from the front of an
 *  entry: `schedule`, `book`, `add`. (`got` only ever appeared as "got a"/
 *  "got an" — there was never a bare form.) Each is also an ordinary English
 *  word, so in principle any of them could eat the first word of a real title.
 *  In practice only one of them does, and the difference is whether the verb
 *  forms a common compound noun with what follows:
 *
 *    book  — book fair, book club, book report, book drive, book swap. Common,
 *            and school-calendar-shaped, which is exactly what she types here.
 *            "book fair all day thursday" committed as "Fair".
 *    add   — no realistic compound. "Add" is not the first word of an event.
 *    schedule — "schedule change" exists but is vanishingly rare as a calendar
 *            entry, and guarding it would turn "schedule karate friday" into
 *            "Schedule karate", a change nobody asked for.
 *
 *  So `book` is guarded and the other two are left exactly as they were. This
 *  is deliberately the smallest change that fixes the reported bug: widening
 *  it to add/schedule would trade a real defect for a speculative one.
 *
 *  The article forms ("book a", "book an") stay in the unconditional list
 *  above — an article is unambiguous evidence of a command, and nothing reads
 *  "book a fair" as a noun phrase.
 *
 *  Why this matters more than a wordy title: the fast path SILENT-COMMITS. No
 *  review chip. And the residue gate can't catch it, because "Fair" is a
 *  perfectly clean noun phrase. Same failure class the gate was built for in
 *  #88, arriving through a door the gate doesn't watch. */
const _BARE_COMMAND_VERBS = ["book"] as const;

/** Determiners that make the following word an object, not a compound noun.
 *  "book my flight" is a command; "book fair" is a thing at school. */
const _DETERMINER_AFTER_VERB_RE =
  /^(a|an|the|my|our|his|her|their|your|its|this|that|these|those|some|another|\w+'s)\b/i;

/** Strip a bare command verb from the front ONLY when what follows looks like
 *  its object rather than the rest of a compound noun.
 *
 *  Two pieces of evidence count, and only these two:
 *    1. a determiner/possessive follows  — "book my flight", "add Carson's game"
 *    2. the remainder contains a known appointment noun — "schedule dentist"
 *
 *  Anything else keeps the verb. Guessing wrong in that direction costs her a
 *  slightly wordy title; guessing wrong in the other direction silently
 *  renames her kid's event, which is the bug this exists to stop. */
/** True when `title` still begins with a verb from _BARE_COMMAND_VERBS.
 *
 *  Only reachable when stripBareCommandVerb already looked at that word and
 *  chose to KEEP it — i.e. it judged the word part of a compound noun. So this
 *  reads as "the leading word is a noun here, whatever the dictionary says."
 *  Callers use it to suppress verb-shaped heuristics (#591). */
export function startsWithGuardedBareVerb(title: string): boolean {
  const lower = (title || "").trim().toLowerCase();
  return _BARE_COMMAND_VERBS.some(
    (verb) => lower === verb || lower.startsWith(`${verb} `)
  );
}

function stripBareCommandVerb(text: string): string {
  for (const verb of _BARE_COMMAND_VERBS) {
    const re = new RegExp(`^\\s*${verb}\\b`, "i");
    if (!re.test(text)) continue;
    const remainder = text.replace(re, "").replace(/^\s+/, "");
    if (!remainder) return text;
    if (
      _DETERMINER_AFTER_VERB_RE.test(remainder) ||
      containsAppointmentNoun(remainder)
    ) {
      return remainder;
    }
    return text;
  }
  return text;
}

/** Strip conversational filler from free-text so the real subject can be
 *  extracted. Runs the anywhere-in-string filler list, then iteratively peels
 *  prefix fillers off the front (so chained fillers like "Don't forget we have
 *  a…" collapse), then normalizes whitespace. Returns the cleaned string. */
export function stripConversationalFiller(input: string): string {
  let cleanedText = input || "";
  for (const pattern of _FILLER_PATTERNS) {
    cleanedText = cleanedText.replace(pattern, " ");
  }
  // Apply prefix strippers iteratively so chained fillers get peeled off
  // (e.g., "Don't forget we have a..." → "...").
  let prevLen = -1;
  while (cleanedText.length !== prevLen) {
    prevLen = cleanedText.length;
    for (const pattern of _PREFIX_FILLER_PATTERNS) {
      cleanedText = cleanedText.replace(pattern, " ").replace(/^\s+/, "");
    }
    // Bare command verbs run inside the same loop so they still chain with the
    // patterns above ("don't forget to book a dentist appointment friday"),
    // but each one has to earn the strip — see stripBareCommandVerb (#591).
    cleanedText = stripBareCommandVerb(cleanedText);
  }
  return cleanedText.replace(/\s+/g, " ").trim();
}

/** Try to parse a manual update-bar entry as a single add. Returns null if the
 *  text looks like an edit, multi-item batch, or anything else the AI should
 *  handle. Conservative on purpose. */
export function parseSimpleManualAdd(input: string): SimpleAddParsed | null {
  const raw = (input || "").trim();
  if (!raw) return null;
  if (raw.length > 200) return null;
  if (/[\n\r]/.test(raw)) return null;
  if (/\b(cancel|cancelled|delete|remove|move|reschedul|change|swap|update|edit|rename)\b/i.test(raw)) return null;
  if (/\?/.test(raw)) return null;

  // Strip conversational filler so we can extract the real subject (Issue #113:
  // shared with detectRecurrence / detectFarFutureEvent via the helper above).
  let cleanedText = stripConversationalFiller(raw);

  // Reject multi-event inputs (let AI path + chip handle these).
  if ((raw.match(/,/g) || []).length >= 2) return null;
  if ((raw.match(/;/g) || []).length >= 1) return null;
  if (/\band then\b/i.test(raw)) return null;

  // Reject inputs with multiple "and" connectors that suggest multiple events.
  const andEventPattern = /\band\b[^.]*?\b(at|on|tomorrow|today|tonight|monday|tuesday|wednesday|thursday|friday|saturday|sunday)\b/gi;
  const andEventMatches = (raw.match(andEventPattern) || []).length;
  if (andEventMatches >= 2) return null;

  // Need a day word to fast-path (otherwise we don't know when).
  const dayHit = findDayWord(cleanedText);
  if (!dayHit) return null;

  // Reject if cleanedText is too short to be a meaningful event after stripping.
  if (cleanedText.split(/\s+/).length < 2) return null;

  const timeHit = findTime(cleanedText);

  // Vague time words → TBD signal, not rejection. classifyEntry will treat
  // TBD-time items as tasks, which is the right default for vague timing.
  // Lowercase-only (no /i) on purpose: a capitalized "Night"/"Morning" etc.
  // appearing mid-title is a proper noun ("A Night at the Museum"), not a
  // genuine vague-time reference, and must not be stripped (Issue #98).
  const vagueTimeWords = /\b(afternoon|morning|evening|night|tonight|later|noon|midday)\b/;
  const hasVagueTime = vagueTimeWords.test(cleanedText);

  // "all day" / "all-day" → an explicit all-day signal, not a rejection or a
  // TBD. Distinct from vagueTimeWords above: vague words ("morning") mean we
  // don't know the time, but "all day" IS the time — the whole day, matching
  // the existing ALL DAY badge convention (isAllDayTime() in TodayTab.tsx).
  const ALL_DAY_WORDS = /\ball[\s-]?day\b/i;
  const hasAllDayWord = ALL_DAY_WORDS.test(cleanedText);

  let title = cleanedText;
  const ranges: Array<[number, number]> = [];
  if (dayHit) ranges.push(dayHit.range);
  if (timeHit) ranges.push(timeHit.range);
  ranges.sort((a, b) => b[0] - a[0]);
  for (const [start, end] of ranges) {
    title = title.slice(0, start) + " " + title.slice(end);
  }
  title = title
    .replace(/\b(at|on)\b/gi, " ")
    .replace(vagueTimeWords, " ")
    .replace(ALL_DAY_WORDS, " ")
    .replace(/\s{2,}/g, " ")
    .replace(/^[\s\-:,.]+|[\s\-:,.]+$/g, "")
    .trim();

  // Issue #98: strip an orphaned leading article left behind after filler
  // removal. "I have a dentist apt next Monday at 10" → filler strip drops
  // "I have" but leaves the "a" that was grammatically attached to it, so the
  // title committed as "A dentist apt" instead of "Dentist apt". Only fire on a
  // single leading article FOLLOWED BY A LOWERCASE WORD so proper names that
  // legitimately start with an article keep it (e.g. "A Night at the Museum"
  // — capital "N" fails the lookahead). Runs BEFORE the residue gate so the
  // gate never false-positives on a harmless leftover article.
  //
  // The article itself ("a"/"an"/"the") must match case-insensitively, but the
  // lookahead must stay lowercase-only — JS RegExp has no inline flag scoping
  // (no `(?-i:...)`), so a single shared /i flag on one regex can't express
  // "case-insensitive here, case-sensitive there". Split into two checks
  // instead: match the article+whitespace case-insensitively, then test the
  // remainder's first character for lowercase with a separate, flag-less regex.
  const leadingArticleMatch = title.match(/^(a|an|the)\s+/i);
  if (leadingArticleMatch && /^[a-z]/.test(title.slice(leadingArticleMatch[0].length))) {
    title = title.slice(leadingArticleMatch[0].length).trim();
  }

  // Residue quality gate (Issue #88 — July 8 silent-wrong commits). Leftover
  // day words / orphan commas / dangling copulas / intent verbs in the title
  // mean a second event is hiding or the parse mangled — reject to the AI path
  // (which chips net-new events for review) instead of silent-committing.
  const residue = isDirtyResidueTitle(title);
  if (residue.dirty) {
    console.debug("[Today][route] fast-path rejected: dirty residue title", {
      check: residue.check,
      title,
      input: raw,
    });
    return null;
  }

  if (!title || title.length < 2) return null;
  if (/\b(am|pm)\b/i.test(title)) return null;

  const dateIso = dayHit.dateIso;
const rawTime = timeHit ? timeHit.time : (hasAllDayWord ? "all-day" : "TBD");
  // Defensive: findTime already returns canonical form when it matches, but
  // belt-and-suspenders normalize so any non-TBD/all-day value is always
  // "H:MM AM/PM". "all-day" passes through as-is (no digits to normalize).
  const time = rawTime === "TBD" || rawTime === "all-day" ? rawTime : (normalizeTime(rawTime) || rawTime);
  // If the finished title still opens with a guarded bare command verb, that
  // is stripBareCommandVerb having decided the word is part of a compound noun
  // ("book fair"), not a command. Tell classifyEntry so it doesn't re-read it
  // as a task verb — see the leadsWithNoun note there (#591).
  const leadsWithNoun = startsWithGuardedBareVerb(title);
  const category: "event" | "task" = classifyEntry(title, time, leadsWithNoun);
  const displayTitle = title.charAt(0).toUpperCase() + title.slice(1);
  return { title: displayTitle, time, dateIso, category };
}

/** Split a raw input into independent event segments for multi-segment
 *  fast-path. Returns [text] when the input is single-event or contains
 *  signals (edit verbs, "?", "and then") that mean we should NOT split.
 *  Splitting rules:
 *    - 2+ commas AND 2+ comma-parts carry a day word → split on commas
 *    - any semicolon → split on semicolons
 *    - bare "and" with day word on both sides → split on "and"
 *  (Issue #92: the comma and "and" branches use the SAME per-part day-word
 *  validation, from the SAME shared vocabulary, so they never disagree.)
 *  Otherwise return the original text as a single segment so the existing
 *  single-segment fast-path / AI fallback handles it. */
export function splitMultiEventInput(text: string): string[] {
  const raw = (text || "").trim();
  if (!raw) return [raw];

  // Safety guards: never split these — let single fast-path / AI handle.
  if (/\?/.test(raw)) return [raw];
  if (/\band then\b/i.test(raw)) return [raw];
  if (/\b(cancel|cancelled|delete|remove|move|reschedul|change|swap|update|edit|rename)\b/i.test(raw)) {
    return [raw];
  }

  const commaCount = (raw.match(/,/g) || []).length;
  const hasSemicolon = /;/.test(raw);

  if (hasSemicolon) {
    const parts = raw.split(/;/).map((s) => s.trim()).filter(Boolean);
    return parts.length > 1 ? parts : [raw];
  }

  if (commaCount >= 2) {
    const parts = raw.split(/,/).map((s) => s.trim()).filter(Boolean);
    // Per-part day-word validation — mirror the "and"-branch below (Issue #92).
    // Only split when 2+ parts carry a day word; otherwise a plain "milk, eggs,
    // bread thursday" grocery list would shatter into fake single-item events.
    // Comma splits now behave identically to and-splits for partial-day-word
    // inputs (the branch stays self-contained: it never falls through to the
    // and-branch, preserving the original "commaCount>=2 is handled here" flow).
    if (parts.length > 1) {
      const partsWithDays = parts.filter((p) => ANY_DAY_WORD_RE.test(p));
      if (partsWithDays.length >= 2) return parts;
    }
    return [raw];
  }

  // "and" with day word on both sides
  const andParts = raw.split(/\band\b/i).map((s) => s.trim()).filter(Boolean);
  if (andParts.length >= 2) {
    const partsWithDays = andParts.filter((p) => ANY_DAY_WORD_RE.test(p));
    if (partsWithDays.length >= 2) return andParts;
  }

  return [raw];
}
